'use client';

import { useTranslations } from 'next-intl';

import { AnimatedButton } from '@/components/ui/animated-button';
import { LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { ShareModal } from '@/lib/share';
import { cn } from '@/lib/ui/utils';

interface ProposalShareButtonProps {
  proposalPubkey: string;
  proposalTitle: string;
  className?: string;
  iconOnly?: boolean;
  variant?: 'secondary' | 'tertiary';
  /** Forwarded to the trigger button. Use to stop propagation when the
   *  button lives inside a clickable row (the launchpad list view). */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export function ProposalShareButton({
  proposalPubkey,
  proposalTitle,
  className,
  iconOnly = false,
  variant = 'tertiary',
  onClick,
}: ProposalShareButtonProps): React.ReactNode {
  const t = useTranslations('launchpad.share');
  const { network } = useUserNetwork();
  // Build the share URL from the pubkey rather than `window.location.href`
  // so the same button works from anywhere — the detail page (URL identical
  // to the proposal route) and the proposals list (URL is `/proposals`,
  // not the per-proposal page). next-intl middleware handles the locale
  // prefix transparently.
  const url = ((): string => {
    if (typeof window === 'undefined') return '';
    const u = new URL(`/proposals/${proposalPubkey}`, window.location.origin);
    withDemoMode(u, network === 'mainnet' ? 'mainnet' : 'devnet');
    return u.toString();
  })();

  return (
    <ShareModal
      surface={{ kind: 'proposal', proposalPubkey }}
      url={url}
      text={t('tweetText', { proposalTitle })}
      title={t('modalTitle')}
      trigger={
        <AnimatedButton
          variant={variant}
          size="sm"
          prefix={iconOnly ? LinkForwardIconSolid : undefined}
          aria-label={t('button')}
          className={cn('shrink-0', !iconOnly && 'h-8 bg-background-tertiary', className)}
          onClick={onClick}
        >
          {iconOnly ? undefined : t('button')}
        </AnimatedButton>
      }
    />
  );
}
